import {
  splashScreenList,
  SplashScreenSdbTopic,
} from "@components/splash-screen/splash-screen-data";
import { SdbCompany } from "@custom-types/sdb-company-types";
import { ISOTimeString } from "@faro-lotv/ielement-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { ElementType } from "react";

/** The content for a single splash screen */
export interface SplashScreenContent {
  /** The title of the splash screen */
  title: React.ReactNode;

  /** The description of the splash screen */
  description: React.ReactNode;

  /** Properties of the image added in splash screen */
  image: {
    /**
     * The image src of the splash screen
     * It is either a string (for *.png) or an ElementType (for *.svg)
     */
    src: string | ElementType;

    /** The alternative text conveying what the image is */
    altText: string;
  };
}

/** The props for the splash screen */
export interface SplashScreenProps {
  /** The topic of the splash screen */
  topic: SplashScreenSdbTopic;

  /** Minimum role of company to see the splash screen */
  allowedRoles: FaroCoreApiTypes.CompanyRole[];

  /** Optional company features that will prevent splash screen to be shown for companies with these features */
  disallowedFeatures?: FaroCoreApiTypes.FeatureIdentifier[];

  /** Optional company features that are required for the splash screen to be shown */
  requiredFeatures?: FaroCoreApiTypes.FeatureIdentifier[];

  /** The content for the splash screen */
  content: SplashScreenContent[];

  /**
   * Callback to check if the splashscreen should be excluded from the provided company.
   * Provides the company as a parameter and optionally its companyFeatures and if it
   * returns true, the splash screen will be excluded.
   */
  shouldExclude?: (company: SdbCompany | null) => boolean;

  /**
   * Optional start date to determine when the splash screen should start to be shown in production.
   */
  startDate?: ISOTimeString;
}

/** All the possible view status of a splash screen topic */
export type SplashScreenViewStatus =
  /** When user has not seen the splash screen yet */
  | "unseen"

  /** When user has skipped the splash screen by closing it */
  | "skipped"

  /** When user has completed the splash screen */
  | "completed";

interface ShouldShowSplashScreenPayload {
  /** The name of the splashscreen */
  topic: SplashScreenSdbTopic;

  /** The company role for the user */
  userRole: FaroCoreApiTypes.CompanyRole;

  /** The current selected company */
  company: SdbCompany | null;

  /** The company features */
  companyFeatures?: FaroCoreApiTypes.FeatureIdentifier[];

  /** Current date in ISO 8601 format */
  currentDate: ISOTimeString;

  /** Whether the current environment is production */
  isProduction: boolean;
}

/**
 * Determine whether to show the splash screen to user or not.
 * Do not modify the order of the checks unless you are sure about the implications.
 * Current order of checks:
 * - Exclusion check
 * - Start date check
 * - Disallowed features check
 * - Required features check
 * - Role check
 */
export function shouldShowSplashScreen({
  topic,
  userRole,
  company,
  companyFeatures,
  currentDate,
  isProduction,
}: ShouldShowSplashScreenPayload): boolean {
  const splashScreen = splashScreenList[topic];

  if (splashScreen.shouldExclude && splashScreen.shouldExclude(company)) {
    return false;
  }

  // Don't show the splash screen if:
  // - the splash screen has a start date and
  // - the current date is before the start date and
  // - the environment is production
  const startDate = splashScreen.startDate;
  if (
    startDate &&
    currentDate < startDate &&
    isProduction
  ) {
    return false;
  }

  // Don't show the splash screen if the company has one of the disallowed features
  if (
    splashScreen.disallowedFeatures &&
    (!companyFeatures ||
      companyFeatures.some((feature) =>
        splashScreen.disallowedFeatures?.includes(feature)
      ))
  ) {
    return false;
  }

  // Don't show the splash screen if the company is missing one of the required features
  if (
    splashScreen.requiredFeatures &&
    splashScreen.requiredFeatures.length > 0
  ) {
    if (!companyFeatures) {
      return false;
    }

    const hasAllRequiredFeatures = splashScreen.requiredFeatures.every((feature) =>
      companyFeatures.includes(feature)
    );

    if (!hasAllRequiredFeatures) {
      return false;
    }
  }

  return splashScreen.allowedRoles.includes(userRole);
}
